import { useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, Loader2, Lock, Unlock, Users as UsersIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { TopBanner } from '@/components/TopBanner';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { clientsApi, type MyClient } from '@/api/clients.api';

export const Route = createFileRoute('/_authenticated/clientes')({
  component: ClientesPage,
});

const statusBadge: Record<string, { label: string; variant: 'success' | 'destructive' | 'secondary' }> = {
  active: { label: 'Activo', variant: 'success' },
  blocked: { label: 'Bloqueado', variant: 'destructive' },
};

// v1: sin paginación server real; traemos hasta LIST_LIMIT y avisamos si se corta.
const LIST_LIMIT = 100;

function ClientesPage() {
  const qc = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Debounce simple del buscador para no pegarle al backend en cada tecla.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ['clients', 'mine', search],
    queryFn: () => clientsApi.listMine({ search: search || undefined, limit: LIST_LIMIT }),
  });

  const blockMutation = useMutation({
    mutationFn: ({ id, blocked }: { id: string; blocked: boolean }) => clientsApi.setBlocked(id, blocked),
    onSuccess: (_res, vars) => {
      toast.success(vars.blocked ? 'Jugador bloqueado' : 'Jugador desbloqueado');
      qc.invalidateQueries({ queryKey: ['clients', 'mine'] });
    },
    onError: () => toast.error('No se pudo actualizar el estado del jugador'),
  });

  const rows: MyClient[] = data ?? [];
  const atLimit = rows.length >= LIST_LIMIT;

  const columns = useMemo<ColumnDef<MyClient>[]>(
    () => [
      { accessorKey: 'casinoUsername', header: 'Usuario' },
      { accessorKey: 'nombre', header: 'Nombre' },
      {
        accessorKey: 'inviteLabel',
        header: 'Vino del link',
        cell: ({ row }) =>
          row.original.inviteLabel ? (
            <Badge variant="secondary">{row.original.inviteLabel}</Badge>
          ) : (
            <span className="text-xs text-muted-foreground">General</span>
          ),
      },
      {
        accessorKey: 'estatus',
        header: 'Estado',
        cell: ({ row }) => {
          const cfg = statusBadge[row.original.estatus] ?? {
            label: row.original.estatus,
            variant: 'secondary' as const,
          };
          return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Fecha alta',
        cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString('es-AR'),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const client = row.original;
          const isBlocked = client.estatus === 'blocked';
          const pending =
            blockMutation.isPending && blockMutation.variables?.id === client.id;
          return (
            <Button
              variant={isBlocked ? 'outline' : 'destructive'}
              size="sm"
              disabled={pending}
              onClick={() => blockMutation.mutate({ id: client.id, blocked: !isBlocked })}
            >
              {pending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : isBlocked ? (
                <Unlock className="mr-2 h-4 w-4" />
              ) : (
                <Lock className="mr-2 h-4 w-4" />
              )}
              {isBlocked ? 'Desbloquear' : 'Bloquear'}
            </Button>
          );
        },
      },
    ],
    [blockMutation],
  );

  return (
    <>
      <TopBanner
        title="Jugadores"
        subtitle="Tus jugadores invitados"
        searchPlaceholder="Buscar por usuario o nombre..."
        searchValue={searchInput}
        onSearchChange={setSearchInput}
      />

      <div className="flex-1 overflow-auto overflow-x-hidden p-4 lg:p-6">
        {isError ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-10 text-center">
            <AlertTriangle className="h-8 w-8 text-destructive" />
            <div className="text-sm font-medium text-destructive">
              No se pudieron cargar los jugadores
            </div>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              Reintentar
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
              <UsersIcon className="h-4 w-4" />
              {isFetching ? 'Actualizando…' : `${rows.length} jugador(es)`}
            </div>

            <DataTable
              columns={columns}
              data={rows}
              isLoading={isLoading}
              emptyMessage="Todavía no tenés jugadores invitados"
              rowClassName={(row) => (row.estatus === 'blocked' ? 'bg-destructive/10' : undefined)}
            />

            {atLimit && (
              <p className="mt-3 text-xs text-muted-foreground">
                Mostrando los primeros {LIST_LIMIT} — usá el buscador para filtrar.
              </p>
            )}
          </>
        )}
      </div>
    </>
  );
}
